import type { RefCallback } from 'react';
import type React from 'react';
import type { GlobalAttributesT, XstyledProps, ValidationMap } from '@elliemae/ds-props-helpers';
import {
  PropTypes,
  getPropsPerSlotPropTypes,
  globalAttributesPropTypes,
  xstyledPropTypes,
} from '@elliemae/ds-props-helpers';
import type { TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_SHAPES,
  ButtonSizesValuesArray,
  ButtonInteractionStates,
  ButtonSizesValuesString,
  ButtonInteractionStatesValuesString,
  ButtonTypesValuesArray,
  ButtonTypesValuesString,
  ButtonShapesValuesArray,
  ButtonShapesValuesString,
} from '../../constants.js';
import { DSButtonDataTestId, DSButtonName, DSButtonSlots } from './DSButtonDefinitions.js';

export declare namespace DSButtonT {
  export type HTMLButtonTypeProp = 'button' | 'submit' | 'reset';

  export type ButtonSizesT = (typeof BUTTON_SIZES)[keyof typeof BUTTON_SIZES];

  export type ButtonTypesT = (typeof BUTTON_TYPES)[keyof typeof BUTTON_TYPES];

  export type ButtonShapesT = (typeof BUTTON_SHAPES)[keyof typeof BUTTON_SHAPES];

  export type ButtonInteractionStatesT = (typeof ButtonInteractionStates)[number];

  export interface DefaultProps {
    'data-testid': string;
    size: ButtonSizesT;
    type: HTMLButtonTypeProp;
    buttonType: ButtonTypesT;
    onClick: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void;
    shape: ButtonShapesT;
    applyAriaDisabled: boolean;
    readOnly: boolean;
  }
  export interface OptionalProps extends TypescriptHelpersT.PropsForGlobalOnSlots<
    typeof DSButtonName,
    typeof DSButtonSlots
  > {
    innerRef?: React.MutableRefObject<HTMLButtonElement | null> | RefCallback<HTMLButtonElement>;
  }
  export interface RequiredProps {
    children: React.ReactNode | React.ReactNode[];
    isV3: boolean;
  }

  export interface Props
    extends
      Partial<DefaultProps>,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLButtonElement>, keyof DefaultProps | keyof OptionalProps | keyof RequiredProps>,
      XstyledProps,
      RequiredProps {}

  export interface InternalProps
    extends
      DefaultProps,
      OptionalProps,
      Omit<GlobalAttributesT<HTMLButtonElement>, keyof DefaultProps | keyof OptionalProps | keyof RequiredProps>,
      XstyledProps,
      RequiredProps {}

  export interface StyledButtonInheritedDrilledProps
    extends
      Omit<GlobalAttributesT<HTMLButtonElement>, keyof DefaultProps | keyof OptionalProps | keyof RequiredProps>,
      XstyledProps {
    type: DefaultProps['type'];
    innerRef?: OptionalProps['innerRef'];
    onClick: DefaultProps['onClick'];
  }
}

export const defaultProps: DSButtonT.DefaultProps = {
  size: BUTTON_SIZES.M,
  buttonType: BUTTON_TYPES.FILLED,
  shape: BUTTON_SHAPES.DEFAULT,
  'data-testid': DSButtonDataTestId.ROOT,
  type: 'button',
  onClick: () => {},
  applyAriaDisabled: false,
  readOnly: false,
};

export const DSButtonPropTypes = {
  ...getPropsPerSlotPropTypes(DSButtonName, DSButtonSlots),
  ...globalAttributesPropTypes,
  ...xstyledPropTypes,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).description(
    'String, Number and/or Dimsum Icon.',
  ).isRequired,
  buttonType: PropTypes.oneOf(ButtonTypesValuesArray)
    .description(ButtonTypesValuesString)
    .defaultValue(BUTTON_TYPES.FILLED),
  shape: PropTypes.oneOf(ButtonShapesValuesArray)
    .description(ButtonShapesValuesString)
    .defaultValue(BUTTON_SHAPES.DEFAULT),
  type: PropTypes.string.description('Html "type" props.').defaultValue('button'),
  size: PropTypes.oneOf(ButtonSizesValuesArray)
    .description(
      `${ButtonSizesValuesString}. Size "s" will be deprecated in the future, so its use is not recommended.`,
    )
    .defaultValue(BUTTON_SIZES.M),
  onClick: PropTypes.func
    .description('Function executed when the button is activated (via keyboard "space" or enter OR via mouse).')
    .signature('(( e: React.SyntheticEvent ) => void )')
    .defaultValue(() => {}),
  innerRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]).description('Inner ref to button component.'),
  'data-testid': PropTypes.string.description('Root element data-testid.').defaultValue(DSButtonDataTestId.ROOT),
  'data-testemulatestate': PropTypes.oneOf(ButtonInteractionStates).description(
    `Emulates button states. Useful for e2e tests. ${ButtonInteractionStatesValuesString}`,
  ),
  applyAriaDisabled: PropTypes.bool
    .description(
      'Whether to apply disabled styling and announce as disabled in SR. CAN STILL RECEIVE FOCUS. PREVENTS ONLY MAIN ACTION.',
    )
    .defaultValue(false),
  readOnly: PropTypes.bool
    .description(
      'Focusable-but-locked state. Emits aria-disabled="true" (buttons are not valid hosts for readonly/aria-readonly, so neither is rendered) and prevents the main action, mirroring applyAriaDisabled. CAN STILL RECEIVE FOCUS.',
    )
    .defaultValue(false),
} as ValidationMap<unknown>;
